import { TRACKABLE_EVENTS, trackEventBudget } from 'helpers/trackerHelpers';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CommentT } from 'routes/Dashboard/components/ChartActions/CommentSection/Comment';
import { RQAPICall, RQAPICallWithData } from 'store/APIClient';

export const useCreateInscriptionComment = (
    inscriptionId: number,
    setDisableButton: React.Dispatch<React.SetStateAction<boolean>>,
): { mutate: (message: string) => void } => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (message: string) => {
            const response = await RQAPICallWithData<CommentT>({
                endpoint: `budget/inscription/${inscriptionId}/comments`,
                method: 'POST',
                body: { content: message },
            });
            return response.content.data;
        },

        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['inscription_comments', inscriptionId],
            });
            queryClient.invalidateQueries({
                queryKey: ['comment_per_inscription'],
            });
            setDisableButton(false);
            trackEventBudget(TRACKABLE_EVENTS.budget.general.addComment);
        },

        onError: () => setDisableButton(false),
    });
};

export const useDeleteInscriptionComment = (
    inscriptionId?: number,
): { mutate: (commentId: number) => void } => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (commentId: number) =>
            RQAPICall({
                endpoint: `budget/inscription/comments/${commentId}`,
                method: 'DELETE',
            }),

        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['inscription_comments', inscriptionId],
            });
            queryClient.invalidateQueries({
                queryKey: ['comment_per_inscription'],
            });
        },
    });
};

export const useUpdateInscriptionComment = (
    setIsEditing: React.Dispatch<React.SetStateAction<boolean>>,
    setDisableEditButton: React.Dispatch<React.SetStateAction<boolean>>,
    inscriptionId?: number,
): {
    mutate: ({
        commentId,
        message,
    }: {
        commentId: number;
        message: string;
    }) => void;
} => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            commentId,
            message,
        }: {
            commentId: number;
            message: string;
        }) => {
            const response = await RQAPICallWithData<CommentT>({
                endpoint: `budget/inscription/comments/${commentId}`,
                method: 'PUT',
                body: { content: message },
            });
            return response.content.data;
        },

        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['inscription_comments', inscriptionId],
            });
            setIsEditing(false);
        },

        onSettled: () => setDisableEditButton(false),
    });
};
